import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import moment from "moment";
import imageCompression from "browser-image-compression";
import { pdf } from "@react-pdf/renderer";
import { DownloadOutlined } from "@ant-design/icons";
import { Button } from "antd";
import { raffleCouponDetails } from "../../../controllers/V1/promocodes/promocodeController";
import { getPublicContent } from "../../../controllers/V1/raffleCoupon/raffleCouponContentController";
import BuyWinCouponPdf from "./BuyWinCouponPdf";
import { DEFAULT_COUPON_CONTENT, mergeCouponContent } from "./couponContentDefaults";
import BuyWinCouponCard, {
  CouponSkeleton,
  pageStyle,
  COUPON_LAYOUT_WIDTH,
  resolveImageUrl,
} from "./BuyWinCouponCard";

const MIN_PDF_BYTES = 500 * 1024;
const MAX_PDF_BYTES = 1024 * 1024;

const PDF_IMAGE_PRESETS = {
  ultra: {
    raffle: {
      maxWidthOrHeight: 1800,
      maxSizeMB: 0.86,
      initialQuality: 0.97,
      fileType: "image/jpeg",
    },
    logo: {
      maxWidthOrHeight: 400,
      maxSizeMB: 0.1,
      initialQuality: 0.98,
      fileType: "image/png",
    },
  },
  premium: {
    raffle: {
      maxWidthOrHeight: 1600,
      maxSizeMB: 0.78,
      initialQuality: 0.95,
      fileType: "image/jpeg",
    },
    logo: {
      maxWidthOrHeight: 360,
      maxSizeMB: 0.09,
      initialQuality: 0.96,
      fileType: "image/png",
    },
  },
  high: {
    raffle: {
      maxWidthOrHeight: 1400,
      maxSizeMB: 0.68,
      initialQuality: 0.92,
      fileType: "image/jpeg",
    },
    logo: {
      maxWidthOrHeight: 300,
      maxSizeMB: 0.08,
      initialQuality: 0.94,
      fileType: "image/png",
    },
  },
  standard: {
    raffle: {
      maxWidthOrHeight: 1200,
      maxSizeMB: 0.58,
      initialQuality: 0.88,
      fileType: "image/jpeg",
    },
    logo: {
      maxWidthOrHeight: 240,
      maxSizeMB: 0.07,
      initialQuality: 0.9,
      fileType: "image/png",
    },
  },
  compact: {
    raffle: {
      maxWidthOrHeight: 1000,
      maxSizeMB: 0.5,
      initialQuality: 0.84,
      fileType: "image/jpeg",
    },
    logo: {
      maxWidthOrHeight: 200,
      maxSizeMB: 0.06,
      initialQuality: 0.88,
      fileType: "image/png",
    },
  },
};

const PDF_PRESET_ORDER = ["ultra", "premium", "high", "standard", "compact"];

const compressImageForPdf = async (url, options = {}) => {
  if (!url) return "";

  const {
    maxWidthOrHeight = 720,
    maxSizeMB = 0.72,
    initialQuality = 1,
    fileType = "image/jpeg",
  } = options;

  try {
    const response = await fetch(url);
    if (!response.ok) return url;
    const blob = await response.blob();
    const file = new File([blob], "image", { type: blob.type || fileType });
    const compressed = await imageCompression(file, {
      maxWidthOrHeight,
      maxSizeMB,
      initialQuality,
      useWebWorker: true,
      fileType,
      alwaysKeepResolution: false,
    });
    return await imageCompression.getDataUrlFromFile(compressed);
  } catch {
    return url;
  }
};

const loadPdfImages = async (assetBase, preset = PDF_IMAGE_PRESETS.premium, content = DEFAULT_COUPON_CONTENT) => {
  const rafflePath = resolveImageUrl(content.raffle_background_image);
  const logoPath = resolveImageUrl(content.logo_image);
  const [raffleImage, logoImage] = await Promise.all([
    compressImageForPdf(rafflePath, preset.raffle),
    compressImageForPdf(logoPath, preset.logo),
  ]);

  return {
    raffleBg: raffleImage,
    logoSrc: logoImage,
  };
};

const buildPdfBlob = async (couponPdfData, images) =>
  pdf(<BuyWinCouponPdf couponData={{ ...couponPdfData, ...images }} />).toBlob();

const selectOptimizedPdf = async (assetBase, couponPdfData, content = DEFAULT_COUPON_CONTENT) => {
  let bestUnderMax = null;

  for (const presetKey of PDF_PRESET_ORDER) {
    const images = await loadPdfImages(assetBase, PDF_IMAGE_PRESETS[presetKey], content);
    const blob = await buildPdfBlob(couponPdfData, images);

    if (blob.size > MAX_PDF_BYTES) continue;

    if (!bestUnderMax || blob.size > bestUnderMax.blob.size) {
      bestUnderMax = { images, blob };
    }

    if (blob.size >= MIN_PDF_BYTES) {
      return { images, blob };
    }
  }

  if (bestUnderMax) return bestUnderMax;

  const images = await loadPdfImages(assetBase, PDF_IMAGE_PRESETS.compact, content);
  return {
    images,
    blob: await buildPdfBlob(couponPdfData, images),
  };
};

const BuyWinCoupon = () => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const slNo = queryParams.get("sl_no") || "";
  const autoDownload = queryParams.get("autoDownload") || "";

  const [orderData, setOrderData] = useState(null);
  const [drawData, setDrawData] = useState(null);
  const [couponContent, setCouponContent] = useState(DEFAULT_COUPON_CONTENT);
  const [raffleBg, setRaffleBg] = useState("");
  const [logoSrc, setLogoSrc] = useState("");
  const [pdfImagesReady, setPdfImagesReady] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isDownloading, setIsDownloading] = useState(false);
  const [error, setError] = useState("");
  const autoDownloadTriggered = useRef(false);

  useEffect(() => {
    document.title = couponContent.page_title || DEFAULT_COUPON_CONTENT.page_title;
  }, [couponContent.page_title]);

  useEffect(() => {
    const fetchCouponContent = async () => {
      const res = await getPublicContent();
      if (res.status === true && res.result) {
        setCouponContent(mergeCouponContent(res.result));
      }
    };

    fetchCouponContent();
  }, []);

  useEffect(() => {
    const fetchCouponDetails = async () => {
      if (!slNo) {
        setError("Invalid coupon code.");
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        setError("");
        const res = await raffleCouponDetails(slNo);
        if (res.status === true && res.result?.orderData) {
          setOrderData(res.result.orderData);
          setDrawData(res.result.drawData || null);
        } else {
          setError(res.message || "Coupon not found.");
        }
      } catch (err) {
        setError("Unable to load coupon. Please try again later.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchCouponDetails();
  }, [slNo]);

  useEffect(() => {
    const preparePdfImages = async () => {
      const assetBase = `${window.location.origin}${process.env.PUBLIC_URL || ""}`;
      const images = await loadPdfImages(assetBase, PDF_IMAGE_PRESETS.premium, couponContent);
      setRaffleBg(images.raffleBg);
      setLogoSrc(images.logoSrc);
      setPdfImagesReady(Boolean(images.raffleBg && images.logoSrc));
    };

    preparePdfImages();
  }, [couponContent]);

  const name = [orderData?.buyer_first_name, orderData?.buyer_last_name]
    .filter(Boolean)
    .join(" ");
  const cpr = orderData?.cpr_number || "N/A";
  const order_no = orderData?.order_no || "N/A";
  const mob = orderData?.buyer_user_id?.country_code
    ? `${orderData.buyer_user_id.country_code} ${orderData.buyer_mobile || ""}`.trim()
    : orderData?.buyer_mobile || "";
  const couponCode = orderData?.coupon_code || slNo;
  const drawDateFormatted =
    orderData?.draw_date && orderData.draw_date !== "N/A"
      ? moment(`${orderData.draw_date} ${orderData.draw_time || "00:00"}:00`).format("DD-MM-YYYY hh:mm A")
      : "N/A";
  const promotionFrom = drawData?.from_date
    ? moment(drawData.from_date).format("DD-MM-YYYY")
    : orderData?.promocode_data?.valid_date
      ? moment(orderData.promocode_data.valid_date).format("DD-MM-YYYY")
      : "";
  const promotionTo = drawData?.to_date
    ? moment(drawData.to_date).format("DD-MM-YYYY")
    : orderData?.promocode_data?.expire_date
      ? moment(orderData.promocode_data.expire_date).format("DD-MM-YYYY")
      : "";
  const raffleCoupon = orderData?.coupon_code || "";
  const couponPdfData = useMemo(
    () => ({
      order_no,
      name,
      cpr,
      mob,
      couponCode,
      drawDateFormatted,
      promotionFrom,
      promotionTo,
      raffleCoupon,
      raffleBg,
      logoSrc,
      content: couponContent,
    }),
    [order_no, name, cpr, mob, couponCode, drawDateFormatted, promotionFrom, promotionTo, raffleCoupon, raffleBg, logoSrc, couponContent]
  );

  const pdfFileName = `Buy_Win_Raffle_Coupon_${couponCode}_${moment(new Date()).format("DD-MM-YYYY")}.pdf`;

  const handleDownloadPdf = useCallback(async () => {
    if (!orderData) return;

    try {
      setIsDownloading(true);
      const assetBase = `${window.location.origin}${process.env.PUBLIC_URL || ""}`;
      const { blob } = await selectOptimizedPdf(assetBase, couponPdfData, couponContent);

      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = pdfFileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("PDF download failed:", err);
    } finally {
      setIsDownloading(false);
    }
  }, [orderData, couponPdfData, pdfFileName, couponContent]);

  useEffect(() => {
    if (
      !isLoading &&
      orderData &&
      pdfImagesReady &&
      autoDownload === "Y" &&
      !autoDownloadTriggered.current
    ) {
      autoDownloadTriggered.current = true;
      handleDownloadPdf();
    }
  }, [isLoading, orderData, pdfImagesReady, autoDownload, handleDownloadPdf]);

  if (isLoading) {
    return <CouponSkeleton />;
  }

  if (error) {
    return (
      <div
        style={{
          margin: 0,
          padding: "24px 16px",
          minHeight: "100vh",
          backgroundColor: "#d5d5d5",
          boxSizing: "border-box",
          fontFamily: "Arial, Helvetica, sans-serif",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <div
          style={{
            backgroundColor: "#fff",
            padding: "24px 32px",
            borderRadius: "8px",
            boxShadow: "0 4px 16px rgba(0,0,0,0.15)",
            textAlign: "center",
            maxWidth: "420px",
          }}
        >
          <p style={{ margin: 0, fontSize: "16px", color: "#a01820", fontWeight: "700" }}>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div style={pageStyle}>
      <div
        style={{
          width: COUPON_LAYOUT_WIDTH,
          maxWidth: "100%",
          margin: "0 auto 16px",
          display: "flex",
          justifyContent: "flex-end",
        }}
      >
        <Button
          type="primary"
          icon={<DownloadOutlined />}
          onClick={handleDownloadPdf}
          loading={isDownloading || !pdfImagesReady}
          disabled={!pdfImagesReady}
          style={{ backgroundColor: "#a01820", borderColor: "#a01820" }}
        >
          Download PDF
        </Button>
      </div>
      <BuyWinCouponCard
        order_no={order_no}
        name={name}
        cpr={cpr}
        mob={mob}
        raffleCoupon={raffleCoupon}
        drawDateFormatted={drawDateFormatted}
        promotionFrom={promotionFrom}
        promotionTo={promotionTo}
        couponContent={couponContent}
      />
    </div>
  );
};

export default BuyWinCoupon;
